<template>
  <div
    v-if="options.length > 0"
    v-on-click-away="hideMenu"
    class="vcv-select"
    :class="[`menu-${menuPosition}`, { big: isBig, lite }]"
  >
    <div class="vcv-select__label" :class="{ active: isMenuShow }" @click="isMenuShow = !isMenuShow">
      {{ computedLabel }}
      <div class="vcv-select__label-icon">
        <i class="icon-caret"></i>
      </div>

      <div v-if="isMultiselect && isShowBadge && selected.length > 0" class="vcv-select__badge">
        {{ selected.length }}
      </div>
    </div>

    <transition name="slide-fade">
      <div v-show="isMenuShow" class="vcv-select__menu" :style="{ width: `${menuWidth}px` }">
        <div v-if="isSearch" class="vcv-select__menu-search">
          <i v-if="searchWord.length > 0" class="icon icon-close" @click="searchWord = ''"></i>

          <i v-else class="icon icon-loupe"></i>

          <input v-model="searchWord" type="text" />
        </div>

        <div v-if="isSearch && searchWord.length > 0 && searchedOptions.length === 0" class="vcv-select__menu-noresult">
          {{ $tr('common.selectNoResult') }}
        </div>

        <div v-else class="vcv-select__menu-container">
          <div v-if="isMultiselect" class="vcv-select__menu-item all" @click="toggleAll">
            <span>{{ isAllSelected ? $tr('common.deselect-all') : $tr('common.select-all') }}</span>

            <i v-if="isMultiselect && isAllSelected" class="icon-check"></i>
          </div>

          <div
            v-for="item in searchedOptions"
            :key="item.value"
            class="vcv-select__menu-item"
            :class="{ active: isMultiselect ? selected.includes(item.value) : selected === item.value }"
            @click="select(item.value)"
          >
            <span>{{ item.text }}</span>

            <i v-if="isMultiselect ? selected.includes(item.value) : selected === item.value" class="icon-check"></i>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { declOfNum } from '@/_helpers/utils.js';
import { directive as VueClickAway } from 'vue-clickaway';
import { isEqual } from 'lodash';

export default {
  directives: {
    onClickAway: VueClickAway
  },

  props: {
    label: {
      type: String,
      default: ''
    },

    // { value, text }
    options: {
      type: Array,
      default: () => []
    },

    selected: {
      type: [Number, Array], // Array [value, value] if isMultiselect, Number if single choice
      default: 0
    },

    isMultiselect: {
      type: Boolean,
      default: false
    },

    isShowBadge: {
      type: Boolean,
      default: false
    },

    menuPosition: {
      type: String,
      default: 'left'
    },

    isBig: {
      type: Boolean,
      default: false
    },

    lite: {
      type: Boolean,
      default: false
    },

    isSearch: {
      type: Boolean,
      default: false
    },

    menuWidth: {
      type: [Number, String],
      default: 'auto'
    }
  },

  data() {
    return {
      isMenuShow: false,
      searchWord: ''
    };
  },

  computed: {
    searchedOptions() {
      if (this.searchWord.length > 0) {
        return this.options.filter(x => x.text.toLowerCase().indexOf(this.searchWord.toLowerCase()) >= 0);
      } else {
        return this.options;
      }
    },

    isAllSelected() {
      const options = this.searchedOptions.map(x => x.value);

      return isEqual(options, this.selected);
    },

    computedLabel() {
      if (!this.isMultiselect && this.selected !== undefined && this.selected !== '') {
        const selectedOption = this.options.find(x => x.value == this.selected);

        if (selectedOption !== undefined) {
          return selectedOption.text;
        } else {
          return this.label;
        }
      } else if (this.isMultiselect && this.selected.length > 0) {
        return this.$tr('common.options-selected', {
          number: this.selected.length,
          variant: declOfNum(this.selected.length, [
            this.$tr('common.1-variant'),
            this.$tr('common.2-variant'),
            this.$tr('common.5-variant')
          ])
        });
      } else {
        return this.label;
      }
    }
  },

  methods: {
    select(newValue) {
      if (this.isMultiselect) {
        let newSelectedValues = [...this.selected];

        if (!this.selected.includes(newValue)) {
          newSelectedValues.push(newValue);
        } else {
          const index = newSelectedValues.findIndex(x => x === newValue);

          newSelectedValues.splice(index, 1);
        }

        this.$emit('select', newSelectedValues);
      } else {
        this.isMenuShow = false;
        this.$emit('select', newValue);
      }
    },

    toggleAll() {
      if (this.isAllSelected) {
        this.$emit('select', []);
      } else {
        const allValues = this.searchedOptions.map(x => x.value);

        this.$emit('select', allValues);
      }
    },

    hideMenu() {
      this.isMenuShow = false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/fonts.scss';

.vcv-select {
  position: relative;
  display: inline-flex;

  &__label-icon {
    width: 40px;
    height: 40px;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s ease-in-out;
  }

  &__label {
    @include mixin-text-main;
    height: 40px;
    width: 100%;
    padding: 0 0 0 15px;
    background: $white;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    white-space: nowrap;
    border: 1px solid $grey-light;
    cursor: pointer;
    position: relative;
    transition: background 0.3s;

    &:hover {
      background: $grey-light;
      transition: background 0.3s;
    }

    &.active {
      background: $grey-lighter;
      transition: background 0.3s;

      .vcv-select__label-icon {
        transform: rotate(180deg);
        transition: transform 0.2s ease-in-out;
      }
    }
  }

  &__badge {
    @include mixin-text-tag;
    font-size: 10px;
    background: $green;
    padding: 0 5px;
    border-radius: 10px;
    color: $white;
    position: absolute;
    min-width: 15px;
    top: -3px;
    left: -3px;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
  }

  &__menu-container {
    max-height: 236px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }

  &__menu {
    position: absolute;
    border: 1px solid $grey-light;
    min-width: 180px;
    max-width: 350px;
    display: flex;
    flex-direction: column;
    top: 45px;
    right: 0;
    width: 100%;
    border-radius: 4px;
    background: $white;
    box-shadow: $box-shadow;
    z-index: 100;
  }

  &__menu-search {
    padding: 10px 15px;
    position: relative;
    display: flex;

    input {
      @include mixin-text-main;
      border: 1px solid $grey-light;
      width: 100%;
      height: 36px;

      &:focus {
        border: 1px solid $green;
      }
    }

    i {
      position: absolute;
      right: 27px;
      top: 20px;
      color: $grey;
      pointer-events: none;
      transition: color 0.2s;

      &:hover {
        color: $green;
        transition: color 0.2s;
      }

      &.icon-close {
        pointer-events: auto;
        font-size: 14px;
        top: 21px;
        cursor: pointer;
      }
    }
  }

  &.menu {
    &-right {
      .vcv-select__menu {
        left: 0;
        right: auto;
      }
    }
  }

  &__menu-item {
    @include mixin-text-main;
    padding: 10px 15px;
    background: $white;
    display: flex;
    line-height: 1.4;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
    transition: background 0.3s, color 0.3s;

    i {
      color: $green;
    }

    &.active {
      background: $grey-lighter;
      transition: background 0.3s;
    }

    &:hover {
      background: $grey-light;
      transition: background 0.3s;
    }

    &.all {
      border-bottom: 1px solid $grey-light;
    }
  }

  &__menu-noresult {
    @include mixin-text-main;
    padding: 40px 15px;
    text-align: center;
  }

  // options
  &.big {
    .vcv-select__label {
      @include mixin-text-header-big;
      box-shadow: none;
    }
  }

  &.lite {
    .vcv-select__label-icon {
      font-size: 10px;
      height: 30px;
      width: 24px;
    }

    .vcv-select__label {
      @include mixin-text-tag;
      height: 30px;
      padding: 0 0 0 10px;
      display: flex;
      align-items: center;
      white-space: nowrap;
      border-radius: 0;
      border-bottom: 1px solid $grey-light;
      cursor: pointer;
      position: relative;
      box-shadow: none;
      transition: background 0.3s;

      &:hover {
        background: $grey-light;
        transition: background 0.3s;
      }

      &.active {
        background: $grey-light;

        .vcv-select__label-icon {
          transform: rotate(180deg);
          transition: transform 0.2s ease-in-out;
        }
      }
    }

    .vcv-select__menu {
      top: 35px;
    }

    .vcv-select__menu-item {
      @include mixin-text-tag;
    }
  }
}
</style>
